import React, { useCallback, useEffect, useState } from 'react'
import { CfoUnderstandingPanel } from './CfoUnderstandingPanel'
import { CfoContextRulesManager } from './CfoContextRulesManager'
import type { UnderstandingClaim } from './cfoUnderstanding'
import type { ServerApiClient } from '../../../data/ServerApiClient'
import './CfoUnderstandingPanel.css'

// CfoAnalysisProfilePage (plan Part 4): the in-shell page that surfaces the per-client belief ledger.
// It owns the fetch lifecycle (loading / error / claims) and delegates rendering to the presentational
// CfoUnderstandingPanel. The loader + the governance mutators are INJECTED (not a hard import of
// ServerApiClient) so the page is trivially testable and carries no dependency on the wire client —
// the unified shell binds the real loader + mutators (appApi.getQuickBooksUnderstanding /
// confirmUnderstandingClaim / rejectUnderstandingClaim / redirectUnderstanding).

/** The understanding load result. The loader may return a bare claims array (back-compat / tests) or
 *  this richer shape carrying the served forecast's cash-plausibility verdict (for the banner). */
export type UnderstandingLoadResult = {
  claims: UnderstandingClaim[]
  /** `false` => the served forecast was flagged cash-implausible; the panel shows an "under review"
   *  banner. `undefined`/`null`/`true` => no banner. */
  forecastCashPlausible?: boolean | null
}

export type LoadUnderstanding = (
  workbookId: string,
) => Promise<UnderstandingClaim[] | UnderstandingLoadResult>

/** Governance mutators (plan B3). When omitted, the page is read-only. */
export type UnderstandingMutators = {
  confirm: (claimId: string) => Promise<unknown>
  reject: (claimId: string) => Promise<unknown>
  redirect: (originId: string, instruction: string) => Promise<unknown>
}

export type CfoAnalysisProfilePageProps = {
  workbookId: string
  loadUnderstanding: LoadUnderstanding
  mutators?: UnderstandingMutators
  contextRulesClient?: Pick<
    ServerApiClient,
    | 'listCompanyIntelligenceContextItems'
    | 'createCompanyIntelligenceContextItem'
    | 'reviewCompanyIntelligenceContextItem'
    | 'supersedeCompanyIntelligenceContextItem'
  >
}

export function CfoAnalysisProfilePage({
  workbookId,
  loadUnderstanding,
  mutators,
  contextRulesClient,
}: CfoAnalysisProfilePageProps) {
  const [claims, setClaims] = useState<UnderstandingClaim[]>([])
  const [forecastCashPlausible, setForecastCashPlausible] = useState<boolean | undefined>(undefined)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busyOriginId, setBusyOriginId] = useState<string | null>(null)

  const refetch = useCallback(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    loadUnderstanding(workbookId)
      .then((next) => {
        if (cancelled) return
        // Accept a bare claims array or the richer { claims, forecastCashPlausible } shape.
        const claimsNext = Array.isArray(next) ? next : next.claims
        const verdict = Array.isArray(next) ? undefined : (next.forecastCashPlausible ?? undefined)
        setClaims(claimsNext)
        setForecastCashPlausible(verdict ?? undefined)
        setLoading(false)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(
          err instanceof Error ? err.message : 'Could not load your understanding profile.',
        )
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [workbookId, loadUnderstanding])

  useEffect(() => refetch(), [refetch])

  // Run a governance mutation: mark the fact busy, call it, then refetch so the ledger reflects the
  // new state. Errors are swallowed (the refetch shows the unchanged ledger) — a wrong tap is harmless.
  const runMutation = useCallback(
    (originId: string, op: () => Promise<unknown>) => {
      setBusyOriginId(originId)
      op()
        .catch(() => undefined)
        .finally(() => {
          setBusyOriginId(null)
          refetch()
        })
    },
    [refetch],
  )

  const interactive = mutators
    ? {
        onConfirm: (claimId: string, originId: string) =>
          runMutation(originId, () => mutators.confirm(claimId)),
        onReject: (claimId: string, originId: string) =>
          runMutation(originId, () => mutators.reject(claimId)),
        onRedirect: (originId: string, instruction: string) =>
          runMutation(originId, () => mutators.redirect(originId, instruction)),
        busyOriginId,
      }
    : {}

  return (
    <div className="cu-page">
      {contextRulesClient ? <CfoContextRulesManager workbookId={workbookId} client={contextRulesClient} /> : null}
      <section className="cu-page-account-understanding" aria-labelledby="cu-account-understanding-title">
        <header>
          <h2 id="cu-account-understanding-title">Account understanding</h2>
          <p>Review how FuseGrid classifies accounts and applies metric logic. These beliefs remain distinct from company facts and presentation rules.</p>
        </header>
        <CfoUnderstandingPanel
          claims={claims}
          loading={loading}
          error={error}
          forecastCashPlausible={forecastCashPlausible}
          {...interactive}
        />
      </section>
    </div>
  )
}

export default CfoAnalysisProfilePage
